import { getMarketAddrForProduct } from "../../utils";
import { AssetType } from "../asset-type.types";
import { BaseReader } from "../base-reader";
import {
  MarketTradesByAddrRequestArgs,
  MarketTradesHistorySchema,
  MarketTradesRequestArgs,
  MarketTradesWindowArgs,
  MarketTradesWindowSchema,
  MarketTradeWsMessage,
  MarketTradeWsMessageSchema,
} from "./market-trades.types";

export class MarketTradesReader extends BaseReader {
  /**
   * Get the latest market trades for a given market
   * @param marketName The name of the market to get market trades for
   * @param assetType Product the name belongs to (default perp) — perp and
   * spot derive different addresses for the same name
   * @param limit The number of market trades to get
   * @returns The market trades for the given market
   */
  async getByName({
    marketName,
    assetType = "perp",
    limit,
    fetchOptions,
  }: MarketTradesRequestArgs) {
    const marketAddr = getMarketAddrForProduct(marketName, assetType, this.deps.config.deployment);
    return this.getByAddr({ marketAddr: marketAddr.toString(), limit, fetchOptions });
  }

  /**
   * Get the latest market trades for a given market address (skips
   * name→address derivation; the address already encodes the product)
   * @param marketAddr The address of the market to get market trades for
   * @param limit The number of market trades to get
   * @returns The market trades for the given market
   */
  async getByAddr({ marketAddr, limit, fetchOptions }: MarketTradesByAddrRequestArgs) {
    const queryParams = new URLSearchParams({ market: marketAddr });
    if (limit !== undefined) {
      queryParams.set("limit", limit.toString());
    }

    const response = await this.getRequest({
      schema: MarketTradesHistorySchema,
      url: `${this.deps.config.tradingHttpUrl}/api/v1/trades`,
      queryParams,
      options: fetchOptions,
    });

    return response.data.items;
  }

  /**
   * A perp market's fills between two timestamps, newest first
   * @param minNotionalUsd Only fills worth at least this much
   * @returns The fills in the window
   */
  async getWindow({
    marketAddr,
    fromMs,
    toMs,
    minNotionalUsd,
    limit,
    fetchOptions,
  }: MarketTradesWindowArgs) {
    const queryParams = new URLSearchParams({
      market: marketAddr,
      from_ms: fromMs.toString(),
      to_ms: toMs.toString(),
    });
    if (minNotionalUsd !== undefined) {
      queryParams.set("min_notional_usd", minNotionalUsd.toString());
    }
    if (limit !== undefined) {
      queryParams.set("limit", limit.toString());
    }

    const response = await this.getRequest({
      schema: MarketTradesWindowSchema,
      url: `${this.deps.config.tradingHttpUrl}/api/v1/trades/window`,
      queryParams,
      options: fetchOptions,
    });

    return response.data;
  }

  /**
   * Subscribe to the latest market trades for a given market
   * @param marketName The name of the market to subscribe to
   * @param onData Callback function for received market trades data
   * @param assetType Product the name belongs to (default perp) — perp and
   * spot derive different addresses for the same name
   * @returns A function to unsubscribe from the market trades updates
   */
  subscribeByName(
    marketName: string,
    onData: (data: MarketTradeWsMessage) => void,
    assetType: AssetType = "perp",
  ) {
    const marketAddr = getMarketAddrForProduct(marketName, assetType, this.deps.config.deployment);
    return this.subscribeByAddr(marketAddr.toString(), onData);
  }

  /**
   * Subscribe to the latest market trades for a given market address (skips
   * name→address derivation; the address already encodes the product)
   * @param marketAddr The address of the market to subscribe to
   * @param onData Callback function for received market trades data
   * @returns A function to unsubscribe from the market trades updates
   */
  subscribeByAddr(marketAddr: string, onData: (data: MarketTradeWsMessage) => void) {
    const topic = `trades:${marketAddr}`;

    return this.deps.ws.subscribe(topic, MarketTradeWsMessageSchema, onData);
  }
}
